import type { SeedIsland } from './seedIslands'

/**
 * Numbers, prices and dates in Mandarin.
 *
 * Numbers are where fluency fails first in any language, because a number is the
 * one thing in a sentence that context cannot recover: miss a noun and you can
 * still follow, miss whether the rent was six hundred or six thousand and the
 * conversation is over.
 *
 * Mandarin adds three problems on top. Measure words sit between the number and
 * the thing, so a number never travels alone. 二 and 两 are both "two" and are
 * not interchangeable. And 十四 against 四十 is the same trap as fourteen against
 * forty, with the added cruelty that the tones do most of the distinguishing —
 * exactly what a bad phone line destroys.
 *
 * So the confirmation lines matter as much as the asking ones, and the frames
 * are what get drilled rather than the numbers, which are the one part you swap.
 */
export const NUMBERS_ZH: SeedIsland[] = [
  {
    id: 'isl_shuzi',
    title: 'Numbers and prices',
    titleZh: '数字和价钱',
    useWhen: 'At a counter, on a phone — anywhere a number decides something.',
    tags: ['transactional', 'numbers'],
    hskLevel: 2,
    blocks: [
      [
        '一共多少钱？',
        'yí gòng duō shao qián',
        'How much is it altogether?',
        '一共 — altogether. Without it you may be told the price of one item rather than the total. 一 takes second tone before the fourth-tone 共.',
      ],
      [
        '我要两个，谢谢。',
        'wǒ yào liǎng ge xiè xie',
        "I'll take two, thanks.",
        '两 rather than 二 before a measure word. 二 is for counting aloud and for numbers themselves; 两 is for two *of* something, and the swap is immediately audible.',
      ],
      [
        '您说的是三十还是十三？',
        'nín shuō de shì sān shí hái shi shí sān',
        'Did you say thirty or thirteen?',
        'The most useful line here. Mandarin builds both from the same two characters in opposite order, so a bad line or a fast speaker collapses them.',
      ],
      [
        '麻烦您写下来。',
        'má fan nín xiě xià lai',
        'Could you write it down, please?',
        'Never embarrassing and always faster. 下来 marks the writing as bringing something down onto the page.',
      ],
      [
        '太贵了，我再看看。',
        'tài guì le wǒ zài kàn kan',
        "That's too expensive — I'll keep looking.",
        '我再看看 is the polite exit from any shop. Doubling 看 softens it to "have a bit more of a look".',
      ],
      [
        '能便宜一点吗？',
        'néng pián yi yì diǎn ma',
        'Could you make it a little cheaper?',
        'Expected in a market, out of place in a shop with tickets on the shelf. 一 takes fourth tone before the third-tone 点.',
      ],
    ],
  },
  {
    id: 'isl_riqi',
    title: 'Dates and times',
    titleZh: '时间和日期',
    useWhen: 'Every arrangement rests on this, and none of it can be guessed.',
    tags: ['transactional', 'time'],
    hskLevel: 2,
    blocks: [
      [
        '今天几号？',
        'jīn tiān jǐ hào',
        "What's the date today?",
        '号 is the spoken word for a day of the month; 日 is what you write. Asking with 日 sounds like reading aloud from a form.',
      ],
      [
        '我三月十四号到。',
        'wǒ sān yuè shí sì hào dào',
        'I arrive on the fourteenth of March.',
        'Mandarin states time from the largest unit down — month, then day — and the whole phrase comes before the verb, never after it.',
      ],
      [
        '我在这里住了两年了。',
        'wǒ zài zhè lǐ zhù le liǎng nián le',
        "I've lived here for two years.",
        'The second 了 is what makes it still true. Drop it and you have said you lived here for two years and then stopped.',
      ],
      [
        '从星期一到星期五。',
        'cóng xīng qī yī dào xīng qī wǔ',
        'From Monday to Friday.',
        '从…到 is the fixed pair for any span, of days or of anything else. The days are simply numbered: 星期一 is week-one.',
      ],
      [
        '现在几点？',
        'xiàn zài jǐ diǎn',
        "What time is it?",
        '几 asks for a number under about ten, which is why it fits hours and dates. 多少 is for anything larger.',
      ],
      [
        '下个月我不在。',
        'xià ge yuè wǒ bú zài',
        "I'm away next month.",
        '不 takes second tone before a fourth: bú zài. 不在 is simply "not present" — Mandarin needs no word for "away".',
      ],
    ],
  },
]
